import React, { useState } from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  MdDashboard,
  MdPeople,
  MdReceipt,
  MdRequestQuote,
  MdBarChart,
  MdSettings,
  MdLogout,
  MdBusiness,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdClose,
  MdTrendingUp,
  MdPersonSearch,
  MdNotifications,
  MdInventory2,
  MdShoppingCart,
  MdWhatsapp,
  MdOutlineAppRegistration,
  MdManageAccounts,
  MdToday,
  MdGroups,
} from 'react-icons/md';
import SupportModal from '../SupportModal';
import { useAuth } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import { useConfig } from '../../context/ConfigContext';

const Overlay = styled.div`
  display: none;
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: ${p => p.open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 99;
    backdrop-filter: blur(2px);
  }
`;

const SidebarContainer = styled.aside`
  width: 260px;
  height: 100vh;
  background-color: ${p => p.theme.colors.sidebarBg};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  box-shadow: ${p => p.theme.shadows.sidebar};
  padding: 0 16px;
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    transform: translateX(${p => p.open ? '0' : '-100%'});
    box-shadow: ${p => p.open ? p.theme.shadows.xl : 'none'};
  }
`;

const CloseBtn = styled.button`
  display: none;
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 16px;
    right: 12px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: ${p => p.theme.colors.backgroundDark};
    border: none;
    color: ${p => p.theme.colors.textSecondary};
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 1;
    transition: ${p => p.theme.transitions.fast};
    &:hover { background: ${p => p.theme.colors.border}; }
  }
`;

const Brand = styled.div`
  padding: 24px 8px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin-bottom: 12px;
`;

const LogoBox = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${p => p.theme.colors.primary}50;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  svg {
    font-size: 1.25rem;
    color: white;
  }
`;

const BrandName = styled.div`
  flex: 1;
  min-width: 0;

  h2 {
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${p => p.theme.colors.textPrimary};
    margin: 0 0 2px;
    word-break: break-word;
    letter-spacing: -0.01em;
  }

  span {
    font-size: 0.7rem;
    color: ${p => p.theme.colors.textLight};
    font-weight: 400;
    letter-spacing: 0.01em;
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar { display: none; }
`;

const NavSection = styled.div`
  margin-bottom: 8px;
`;

const NavLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 14px 8px 6px;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  color: ${p => p.theme.colors.sidebarText};
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 12px;
  transition: all 0.2s ease;
  margin: 2px 5px;
  position: relative;

  .icon-box {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${p => p.theme.colors.backgroundDark};
    transition: all 0.2s ease;

    svg {
      font-size: 1.1rem;
      color: ${p => p.theme.colors.textLight};
      transition: color 0.2s ease;
    }
  }

  &:hover {
    color: ${p => p.theme.colors.textPrimary};
    background-color: ${p => p.theme.colors.sidebarHover};

    .icon-box {
      background: ${p => p.theme.colors.border};
      svg { color: ${p => p.theme.colors.textSecondary}; }
    }
  }

  &.active {
    color: ${p => p.theme.colors.textPrimary};
    background-color: ${p => p.theme.colors.backgroundLight};
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    font-weight: 600;

    .icon-box {
      background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
      box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40;
      svg { color: white; }
    }
  }
`;

const Footer = styled.div`
  padding: 12px 0 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const HelpCard = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
`;

const HelpHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  cursor: pointer;
`;

const HelpTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: white;
`;

const HelpToggle = styled.button`
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 5px;
  width: 18px;
  height: 18px;
  min-height: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover { background: rgba(255,255,255,0.35); }
`;

const HelpBody = styled.div`
  padding: ${p => p.open ? '0 10px 8px' : '0'};
  max-height: ${p => p.open ? '80px' : '0'};
  overflow: hidden;
  transition: max-height 0.25s ease, padding 0.25s ease;
`;

const HelpText = styled.div`
  font-size: 0.65rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 6px;
  line-height: 1.3;
`;

const HelpButton = styled.button`
  background: white;
  color: ${p => p.theme.colors.primaryDark};
  border: none;
  border-radius: 6px;
  padding: 3px 10px;
  min-height: unset;
  font-size: 0.68rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 3px 8px rgba(0,0,0,0.15);
    transform: translateY(-1px);
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 4px;
  background: ${p => p.theme.colors.backgroundDark};
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}90, ${p => p.theme.colors.primaryDark}90);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const UserName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${p => p.theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FooterBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  color: ${p => p.theme.colors.textLight};
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 2px;

  svg { font-size: 1rem; flex-shrink: 0; }

  &:hover {
    background-color: ${p => p.theme.colors.backgroundDark};
    color: ${p => p.theme.colors.textSecondary};
  }

  &.logout:hover {
    background-color: rgba(252, 129, 129, 0.1);
    color: ${p => p.theme.colors.danger};
  }
`;

const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const { config } = useConfig();
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(true);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { confirm } = useModal();

  const handleLogout = async () => {
    const ok = await confirm('¿Estás seguro de que deseas cerrar sesión?', {
      title: 'Cerrar Sesión',
      confirmText: 'Cerrar sesión',
      cancelText: 'Cancelar',
    });
    if (ok) {
      logout();
      navigate('/login');
    }
  };

  const isAdmin = user?.rol === 'admin';

  const initials = user?.nombre
    ? user.nombre.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  // Returns true if the tab should be hidden.
  // Por defecto el admin siempre ve todas las pestañas, para no quedarse sin
  // acceso a algo que él mismo ocultó. Si activa "ocultar_tabs_admin" en
  // Configuración, las pestañas ocultas también le aplican a su cuenta.
  const hidden = (() => {
    if (isAdmin && !config?.ocultar_tabs_admin) return () => false;
    try {
      const ocultas = JSON.parse(config?.tabs_ocultas || '[]');
      return (key) => ocultas.includes(key);
    } catch {
      return () => false;
    }
  })();

  return (
    <>
      <Overlay open={isOpen} onClick={onClose} />
      <SidebarContainer open={isOpen}>
        <CloseBtn onClick={onClose}><MdClose /></CloseBtn>
        <Brand>
          <LogoBox>
            {config?.logo_url
              ? <img src={config.logo_url} alt="logo" />
              : <MdBusiness />
            }
          </LogoBox>
          <BrandName>
            <h2>{config?.nombre_empresa || 'Payment Manager'}</h2>
            <span>Panel de gestión</span>
          </BrandName>
        </Brand>

        <Nav>
          {/* Ventas — hide entire section if all items hidden */}
          {!(['pipeline','leads','cotizaciones','inventario','pedidos'].every(hidden)) && (
            <NavSection>
              <NavLabel>Ventas</NavLabel>
              {!hidden('pipeline') && (
                <NavItem to="/pipeline">
                  <div className="icon-box"><MdTrendingUp /></div>
                  <span>Pipeline</span>
                </NavItem>
              )}
              {!hidden('leads') && (
                <NavItem to="/leads">
                  <div className="icon-box"><MdPersonSearch /></div>
                  <span>Leads</span>
                </NavItem>
              )}
              {!hidden('cotizaciones') && (
                <NavItem to="/cotizaciones">
                  <div className="icon-box"><MdRequestQuote /></div>
                  <span>Cotizaciones</span>
                </NavItem>
              )}
              {!hidden('inventario') && (
                <NavItem to="/inventario">
                  <div className="icon-box"><MdInventory2 /></div>
                  <span>{config?.tipo_negocio === 'servicios' ? 'Servicios' : 'Inventario'}</span>
                </NavItem>
              )}
              {!hidden('pedidos') && (
                <NavItem to="/pedidos">
                  <div className="icon-box"><MdShoppingCart /></div>
                  <span>{config?.tipo_negocio === 'servicios' ? 'Proyectos' : 'Pedidos'}</span>
                </NavItem>
              )}
            </NavSection>
          )}

          {!(['clientes','grupos','recibos'].every(hidden)) && (
            <NavSection>
              <NavLabel>Clientes</NavLabel>
              {!hidden('clientes') && (
                <NavItem to="/clientes">
                  <div className="icon-box"><MdPeople /></div>
                  <span>Clientes</span>
                </NavItem>
              )}
              {!hidden('grupos') && (
                <NavItem to="/grupos">
                  <div className="icon-box"><MdGroups /></div>
                  <span>Grupos</span>
                </NavItem>
              )}
              {!hidden('recibos') && (
                <NavItem to="/recibos">
                  <div className="icon-box"><MdReceipt /></div>
                  <span>Recibos</span>
                </NavItem>
              )}
            </NavSection>
          )}

          {!(['alertas','difusion','plantillas'].every(hidden)) && (
            <NavSection>
              <NavLabel>Gestión</NavLabel>
              {!hidden('alertas') && (
                <NavItem to="/alertas">
                  <div className="icon-box"><MdNotifications /></div>
                  <span>Cobranza</span>
                </NavItem>
              )}
              {!hidden('difusion') && (
                <NavItem to="/difusion">
                  <div className="icon-box"><MdWhatsapp /></div>
                  <span>Difusión</span>
                </NavItem>
              )}
              {isAdmin && !hidden('plantillas') && (
                <NavItem to="/plantillas">
                  <div className="icon-box"><MdOutlineAppRegistration /></div>
                  <span>Plantillas WA</span>
                </NavItem>
              )}
            </NavSection>
          )}

          {!(['mi_dia','dashboard','reportes'].every(hidden)) && (
            <NavSection>
              <NavLabel>Análisis</NavLabel>
              {!hidden('mi_dia') && (
                <NavItem to="/mi-dia">
                  <div className="icon-box"><MdToday /></div>
                  <span>Mi Día</span>
                </NavItem>
              )}
              {!hidden('dashboard') && (
                <NavItem to="/" end>
                  <div className="icon-box"><MdDashboard /></div>
                  <span>Dashboard</span>
                </NavItem>
              )}
              {!hidden('reportes') && (
                <NavItem to="/reportes">
                  <div className="icon-box"><MdBarChart /></div>
                  <span>Reportes</span>
                </NavItem>
              )}
            </NavSection>
          )}

          {isAdmin && (
            <NavSection>
              <NavLabel>Sistema</NavLabel>
              <NavItem to="/configuracion">
                <div className="icon-box"><MdSettings /></div>
                <span>Configuración</span>
              </NavItem>
              <NavItem to="/usuarios">
                <div className="icon-box"><MdManageAccounts /></div>
                <span>Usuarios</span>
              </NavItem>
            </NavSection>
          )}
        </Nav>

        <Footer>
          <HelpCard>
            <HelpHeader onClick={() => setHelpOpen(o => !o)}>
              <HelpTitle>¿Necesitas ayuda?</HelpTitle>
              <HelpToggle onClick={e => { e.stopPropagation(); setHelpOpen(o => !o); }}>
                {helpOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
              </HelpToggle>
            </HelpHeader>
            <HelpBody open={helpOpen}>
              <HelpText>Consulta la documentación o contacta al soporte.</HelpText>
              <HelpButton onClick={() => setSupportModalOpen(true)}>
                Soporte
              </HelpButton>
            </HelpBody>
          </HelpCard>

          <UserRow>
            <Avatar>{initials}</Avatar>
            <UserName>{user?.nombre || 'Usuario'}</UserName>
          </UserRow>

          <FooterBtn className="logout" onClick={handleLogout}>
            <MdLogout />
            <span>Cerrar Sesión</span>
          </FooterBtn>
        </Footer>
      </SidebarContainer>

      <SupportModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
